/**
 * Pipeline Stage Types
 *
 * Represents the 11-stage AISecOps processing pipeline:
 * INPUT → PII_SCAN → GUARDRAILS → CLASSIFY → QUEUE → RETRIEVE → LLM → REVIEW → STORAGE → EMBEDDING → RECOMMEND
 *
 * RETRIEVE (Phase S): Hybrid search using pgvector (dense) + BM25 (sparse) + RRF fusion.
 * Injects relevant context from existing notes into the LLM prompt.
 * For SEARCH_ONLY intent, LLM is skipped — retrieval results go directly to REVIEW.
 *
 * RECOMMEND: Post-pipeline enrichment — generates contextual recommendations
 * (related notes, research, action items, topic expansion) after a note reaches
 * COMPLETED status. Chained from generate_embedding via Celery callback.
 */

/**
 * Pipeline stages in processing order.
 * Each stage represents a discrete step in the security-first AI pipeline.
 */
export type PipelineStage =
  | "INPUT"
  | "PII_SCAN"
  | "GUARDRAILS"
  | "CLASSIFY"
  | "QUEUE"
  | "RETRIEVE"
  | "LLM"
  | "REVIEW"
  | "STORAGE"
  | "EMBEDDING"
  | "RECOMMEND";

/**
 * Stage status values for visualization.
 */
export type StageStatus =
  | "pending"
  | "active"
  | "completed"
  | "skipped"
  | "failed";

/**
 * Intent categories from the guardrails classifier (L1 layer).
 * Must match backend IntentCategory enum in schemas.py.
 */
export type IntentCategory =
  | "SUMMARIZE"
  | "EXTRACT"
  | "REASON"
  | "SIMPLE_NOTE"
  | "SEARCH_ONLY";

/**
 * Data specific to the CLASSIFY stage.
 * Shows intent classification results and routing decision.
 */
export interface ClassifyStageData {
  /** Classified intent category */
  intent: IntentCategory;
  /** Classification confidence score (0.0-1.0) */
  confidence: number;
  /** Human-readable routing decision */
  routingDecision: string;
}

/**
 * Complete pipeline state for a note.
 * Derived from Note API response fields.
 */
export interface PipelineState {
  /** Note ID being processed */
  noteId: string;
  /** Status of each pipeline stage */
  stages: Record<PipelineStage, StageStatus>;
  /** CLASSIFY stage details (present when classification complete) */
  classify?: ClassifyStageData;
}

/**
 * Ordered array of all pipeline stages for rendering.
 */
export const PIPELINE_STAGES: PipelineStage[] = [
  "INPUT",
  "PII_SCAN",
  "GUARDRAILS",
  "CLASSIFY",
  "QUEUE",
  "RETRIEVE",
  "LLM",
  "REVIEW",
  "STORAGE",
  "EMBEDDING",
  "RECOMMEND",
];

/**
 * Human-readable labels for each stage.
 */
export const STAGE_LABELS: Record<PipelineStage, string> = {
  INPUT: "Input",
  PII_SCAN: "PII Scan",
  GUARDRAILS: "Guardrails",
  CLASSIFY: "Classify",
  QUEUE: "Queue",
  RETRIEVE: "Retrieve",
  LLM: "LLM",
  REVIEW: "Review",
  STORAGE: "Storage",
  EMBEDDING: "Embedding",
  RECOMMEND: "Recommend",
};

/**
 * Stage descriptions for tooltips.
 */
export const STAGE_DESCRIPTIONS: Record<PipelineStage, string> = {
  INPUT: "Content received from user",
  PII_SCAN: "Scanning for personally identifiable information",
  GUARDRAILS: "Checking for prompt injection attacks",
  CLASSIFY: "Determining intent and routing",
  QUEUE: "Queued for worker processing",
  RETRIEVE: "Hybrid search: pgvector (dense) + BM25 (sparse) → RRF fusion",
  LLM: "Processing with language model",
  REVIEW: "Awaiting user confirmation of LLM output",
  STORAGE: "Saving to database",
  EMBEDDING: "Generating 768-dim vector embedding (pgvector)",
  RECOMMEND: "Generating contextual recommendations",
};

/**
 * Routing decisions based on intent category.
 */
export const ROUTING_DECISIONS: Record<IntentCategory, string> = {
  SIMPLE_NOTE: "Direct Save — No LLM",
  SEARCH_ONLY: "RAG Search Only",
  SUMMARIZE: "Full LLM Pipeline",
  EXTRACT: "LLM + JSON Extraction",
  REASON: "LLM + Chain-of-Thought",
};
